/**
 * docs/LEGAL.md — the three public legal texts: the terms of use, the privacy and KVKK notice,
 * and the risk notice. Written in Turkish and English only (`content/tr.ts`, `content/en.ts`);
 * a visitor reading any other language gets the English text with a note saying so.
 *
 * Plain data, no React: the page (`pages/LegalPage.tsx`) renders it, the prerender writes it
 * into terms.html, privacy.html and risk.html.
 */

import { EN_TEXTS } from './content/en';
import { TR_TEXTS } from './content/tr';

/**
 * The version a sign-up accepts. Bump it, and `TERMS_VERSION` in backend/app/core/legal.py, on
 * every change of wording, and change `updated` in both content files.
 */
export const LEGAL_VERSION = '2026-10-29';

export const LEGAL_DOCS = ['terms', 'privacy', 'risk'] as const;
export type LegalDocKey = (typeof LEGAL_DOCS)[number];

/**
 * A paragraph, or a bullet list. Inside the text `{email}` becomes the contact address, `{terms}`
 * / `{privacy}` / `{risk}` a link to that document under its title — `{risk:’ni}` adds a Turkish
 * case ending inside the link — and `[[…]]` a placeholder still to be filled in, drawn so nobody
 * mistakes it for the final text.
 */
export type LegalBlock = string | readonly string[];

export interface LegalSection {
  /** The section's anchor: `/privacy#rights`. */
  id: string;
  heading: string;
  blocks: readonly LegalBlock[];
}

export interface LegalDocument {
  title: string;
  /** One or two sentences under the title: what the document is for. */
  summary: string;
  sections: readonly LegalSection[];
}

export interface LegalTexts {
  /** The date of `LEGAL_VERSION`, written out in the text's language. */
  updated: string;
  documents: Record<LegalDocKey, LegalDocument>;
}

export type LegalLanguage = 'tr' | 'en';

const TEXTS: Record<LegalLanguage, LegalTexts> = { tr: TR_TEXTS, en: EN_TEXTS };

/** Turkish for a Turkish reader, English for everyone else. */
export function legalLanguage(locale: string | null | undefined): LegalLanguage {
  return locale?.toLowerCase().startsWith('tr') ? 'tr' : 'en';
}

export function legalTexts(language: LegalLanguage): LegalTexts {
  return TEXTS[language];
}

/** The `[[…]]` placeholders a text still carries — empty once the document is complete. */
export function placeholders(document: LegalDocument): string[] {
  const found: string[] = [];
  const scan = (text: string) => {
    for (const match of text.matchAll(/\[\[([^\]]+)\]\]/g)) found.push(match[1]);
  };
  for (const section of document.sections) {
    scan(section.heading);
    for (const block of section.blocks) {
      if (typeof block === 'string') scan(block);
      else block.forEach(scan);
    }
  }
  return found;
}
